import React, { useEffect } from 'react'
import {
  IonButton,
  IonButtons,
  IonFooter,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonViewDidLeave,
  useIonViewWillEnter,
} from '@ionic/react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { chevronBack, chevronForward } from 'ionicons/icons'
import PCRHeader from '../../../components/pcrHeader/PCRHeader.component'
import {
  saveSpecialReport,
  resetSpecialReportsState,
  setCurrentStep,
  setCurrentSubStep,
  initializeSpecialReport,
  setCompleted,
} from '../store/specialReports.actions'
import SpecialReportsContent from '../specialReportsContent/SpecialReports.content'
import Stepper from '../../../components/stepper/Stepper.component'
import { SPECIAL_REPORTS_STEPS } from '../constants'
import Logger from '../../../services/logger.service'
import { goBack } from '../../../utilities/navigation.util'
import './specialReports.page.css'
import { AppRouterConstants } from '../../../components/appRouter/AppRouter.constants'
import { AuthenticationService } from '../../login/services/authentication.service'
import { getUserSelector } from '../../user/selectors'
import { useHistory } from 'react-router'
import { getSpecialReportsSelector } from '../selectors'
import { showToast } from '../../../components/toast/store/toast.actions'
import { STEP_IDS } from '../constants'
import SpecialReportHeaderInfo from './SpecialReportHeaderInfo.component'
import { IServiceConfig } from '../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../configurations/selectors/serviceConfig.selector'
import { showAlert } from '../../../components/alert/store/alert.actions'
import { ToastColor } from '../../../components/toast/store/toast.constants'

const SpecialReports: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const specialReportsState = useSelector(getSpecialReportsSelector)
  const { __currentStep, __currentSubStep } = specialReportsState
  const history = useHistory()
  const user = useSelector(getUserSelector)
  const serviceConfig: IServiceConfig = useSelector(getServiceConfigSelector)

  const allowSecureRoutes = AuthenticationService.allowSecureRoutes(user)

  useEffect(() => {
    if (!allowSecureRoutes) {
      Logger.warn('User is not authenticated. Redirecting to index route.')
      history.push(AppRouterConstants.INDEX_ROUTE)
    }
  }, [allowSecureRoutes, history])

  useIonViewWillEnter(() => {
    dispatch(initializeSpecialReport())
  })

  useIonViewDidLeave(() => {
    dispatch(resetSpecialReportsState())
  })

  const onSaveClick = () => {
    dispatch(saveSpecialReport())
    dispatch(
      showToast({
        message: t('specialReports.saveSuccess'),
        position: 'top',
        color: ToastColor.Success,
      })
    )
    goBack()
  }

  const onStepClick = (stepId: number) => {
    dispatch(setCurrentStep(stepId))
  }

  const onCancel = () => {
    const alertState = {
      header: t('specialReports.exitConfirmationHeader'),
      message: t('specialReports.exitConfirmationMessage'),
      buttons: [
        { text: t('common.buttons.Cancel') },
        {
          text: t('common.buttons.Continue'),
          handler: () => {
            Logger.info(t('specialReports.userCancelled'))
            goBack()
          },
        },
      ],
    }
    dispatch(showAlert(alertState))
  }

  const handlePrevious = () => {
    if (__currentStep === STEP_IDS.DETAILS && __currentSubStep === 2) {
      dispatch(setCurrentSubStep(1))
    } else if (__currentStep === STEP_IDS.QUESTIONS) {
      dispatch(setCurrentStep(STEP_IDS.DETAILS))
      dispatch(setCurrentSubStep(2))
    } else if (__currentStep > STEP_IDS.DETAILS) {
      dispatch(setCurrentStep(__currentStep - 1))
    }
  }

  const handleNext = () => {
    if (__currentStep === SPECIAL_REPORTS_STEPS.length) {
      dispatch(setCompleted(true))
    }
    if (__currentStep === STEP_IDS.DETAILS && __currentSubStep === 1) {
      dispatch(setCurrentSubStep(2))
    } else if (__currentStep < SPECIAL_REPORTS_STEPS.length) {
      dispatch(setCurrentStep(__currentStep + 1))
      dispatch(setCurrentSubStep(1))
    } else {
      onSaveClick()
    }
  }

  const handleNextDisabled = () => {
    const { subject, supervisor, categories, narrative } = specialReportsState
    let isDisabled = false
    if (__currentStep === STEP_IDS.DETAILS) {
      if (__currentSubStep === 1) {
        const isSubjectRequired = serviceConfig.SPECREP_REQ_SUBJECT === 1
        if (isSubjectRequired && (!subject || subject.trim() === '')) {
          isDisabled = true
        }

        const isSupervisorRequired = serviceConfig.SPECREP_REQ_SUP === 1
        if (isSupervisorRequired && !supervisor) {
          isDisabled = true
        }
      }

      if (__currentSubStep === 2) {
        const isCategoryRequired = serviceConfig.SPECREP_REQ_CAT === 1
        if (isCategoryRequired && (!categories || categories.length === 0)) {
          isDisabled = true
        }
      }
    }

    if (__currentStep === STEP_IDS.NARRATIVE) {
      const isNarrativeRequired = serviceConfig.SPECREP_REQ_NARRATIVE === 1
      if (isNarrativeRequired && (!narrative || narrative.trim() === '')) {
        isDisabled = true
      }
    }

    return isDisabled
  }

  const renderFooter = () => {
    const isLastStep = __currentStep === SPECIAL_REPORTS_STEPS.length
    const nextMessage = isLastStep ? 'common.buttons.Complete' : 'common.buttons.Next'

    return (
      <IonFooter className='ion-no-border'>
        <IonToolbar color='light' className='p-10 footer-toolbar-padding'>
          <IonButtons slot='start'>
            <IonButton data-testid='Cancel' expand='block' onClick={onCancel} className='p-5 btn btn-secondary mr-8'>
              {t('common.buttons.Cancel')}
            </IonButton>
          </IonButtons>

          <IonButtons slot='end' className='gap-x-2'>
            <IonButton
              data-testid='Back'
              className='p-5 btn btn-secondary'
              expand='block'
              disabled={__currentStep === STEP_IDS.DETAILS && __currentSubStep === 1}
              onClick={handlePrevious}>
              <IonIcon className='special-reports-footer-button-chevron' icon={chevronBack} />
              {t('common.buttons.Back')}
            </IonButton>
            <IonButton
              data-testid='Next'
              disabled={handleNextDisabled()}
              className='p-5 btn btn-primary'
              expand='block'
              onClick={handleNext}>
              {t(nextMessage)}
              {isLastStep ? null : <IonIcon className='special-reports-footer-button-chevron' icon={chevronForward} />}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    )
  }

  const renderHeader = () => {
    return (
      <>
        <PCRHeader />
        <IonHeader className='ion-no-padding'>
          <IonToolbar color='light'>
            <IonTitle>{t('specialReports.newSpecialReport')}</IonTitle>
          </IonToolbar>
          <IonToolbar color='light'>
            <Stepper steps={SPECIAL_REPORTS_STEPS} currentStep={__currentStep} onStepClick={onStepClick} />
            {__currentStep === STEP_IDS.DETAILS && __currentSubStep === 1 ? null : <SpecialReportHeaderInfo />}
          </IonToolbar>
        </IonHeader>
      </>
    )
  }

  return (
    <IonPage id='SpecialReportsPage'>
      {renderHeader()}
      <SpecialReportsContent />
      {renderFooter()}
    </IonPage>
  )
}

export default SpecialReports
